import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { BASELINE_MIN_CHECK_INS } from "../../../../utils/readiness-report";
import {
  formatChange,
  formatUtcDay,
  sectionTranslator,
  useIntensity,
} from "./readinessFormat";

const COMPLETION_ATTENTION_BELOW_PCT = 75;

const tr = sectionTranslator("tiles");

// Top edge of a tile: amber when it needs attention, red for a completion under 75%, green when
// all is well, neutral when there is nothing to judge yet.
const changeTone = (change) => {
  if (change < 0) return "down";
  return change > 0 ? "up" : "flat";
};

/**
 * Gets the tone for the average score.
 * @param {*} summary
 * @param {*} normal
 * @returns
 */
const averageTone = (summary, normal) => {
  if (summary.avg == null || !normal?.ready) return "neutral";
  return summary.avg < normal.total.lo ? "amber" : "green";
};

/**
 * Gets the tone for the below normal score.
 * @param {*} summary
 * @returns
 */
const belowTone = (summary) => (summary.below > 0 ? "amber" : "green");

/**
 * Gets the tone for the completion percentage.
 * @param {*} pct
 * @returns
 */
const completionTone = (pct) => {
  if (pct == null) return "neutral";
  if (pct < COMPLETION_ATTENTION_BELOW_PCT) return "red";
  return pct < 100 ? "amber" : "green";
};

/**
 * Gets the tone for the latest score.
 * @param {*} status
 * @returns
 */
const latestTone = (status) => {
  if (status === "below") return "amber";
  return status === "within" || status === "above" ? "green" : "neutral";
};

const Tile = ({ label, tone, value, unit, children }) => (
  <div className={`rr-tile rr-tile--${tone}`}>
    <span className="rr-tile__label">{label}</span>
    <span className="rr-tile__value">
      {value}
      {unit ? <span className="rr-tile__unit">{unit}</span> : null}
    </span>
    <span className="rr-tile__sub">{children}</span>
  </div>
);

Tile.propTypes = {
  label: PropTypes.string.isRequired,
  tone: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  unit: PropTypes.string,
  children: PropTypes.node,
};

export const ReadinessKpiTiles = ({ report }) => {
  const { t } = useTranslation("athlete-portal");
  const { context, toMeasure } = useIntensity();
  const { summary, normal, latest } = report;
  const normalRange = normal?.ready ? normal.total : null;
  // A period with no questionnaire has nothing to average, judge or show as the latest
  const answered = report.state !== "empty";

  const latestDetail = latest
    ? [
        tr(t, "latestDetail", {
          date: formatUtcDay(latest.utcDate, "MMM D"),
          status: t(`readinessRate.report.status.${latest.status}`),
        }),
        latest.sessionRpe == null
          ? null
          : tr(t, "latestRpe", { rpe: toMeasure(latest.sessionRpe), context }),
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div className="rr-tiles">
      {answered ? (
        <>
          <Tile
            label={tr(t, "avgScore")}
            tone={averageTone(summary, normal)}
            value={summary.avg == null ? "—" : summary.avg.toFixed(1)}
            unit="/25"
          >
            {normalRange
              ? tr(t, "normalRange", { lo: normalRange.lo, hi: normalRange.hi })
              : tr(t, "learningNormal", {
                  checkIns: normal?.checkIns ?? 0,
                  min: BASELINE_MIN_CHECK_INS,
                })}
            {summary.change == null ? null : (
              <>
                <span
                  className={`rr-pill rr-pill--${changeTone(summary.change)}`}
                >
                  {formatChange(summary.change)}
                </span>
                <span>{tr(t, "vsPrevious")}</span>
              </>
            )}
          </Tile>
          <Tile
            label={tr(t, "belowNormal")}
            tone={belowTone(summary)}
            value={summary.judged === 0 ? "—" : summary.below}
          >
            {summary.judged === 0
              ? tr(t, "noneJudged", { min: BASELINE_MIN_CHECK_INS })
              : tr(t, "ofQuestionnaires", { count: summary.judged })}
          </Tile>
        </>
      ) : null}
      <Tile
        label={tr(t, "completion")}
        tone={completionTone(summary.completionPct)}
        value={
          summary.completionPct == null ? "—" : `${summary.completionPct}%`
        }
      >
        {summary.trainingDays === 0
          ? tr(t, "noTrainingDays")
          : tr(t, "trainingDays", {
              completed: summary.completedTrainingDays,
              count: summary.trainingDays,
            })}
      </Tile>
      {answered ? (
        <Tile
          label={tr(t, "latest")}
          tone={latestTone(latest?.status)}
          value={latest ? latest.total : "—"}
          unit={latest ? "/25" : undefined}
        >
          {latestDetail}
        </Tile>
      ) : null}
    </div>
  );
};

ReadinessKpiTiles.propTypes = {
  report: PropTypes.shape({
    state: PropTypes.string,
    summary: PropTypes.object.isRequired,
    normal: PropTypes.object,
    latest: PropTypes.object,
  }).isRequired,
};
